import React, { useEffect, useMemo } from 'react';
import { withStyles, WithStyles, makeStyles } from '@mui/styles';
import { PharmacyService } from 'services/utils/pharmacy-service';
import { IPharmacy } from 'slices/pharmacies-slice';
import { ShippingStyles } from './shipping.styles';
import { IPrintLabelButton } from './types';
import VoidLabelButton from './void-label-button';
import ReprintLabelButton from './re-print-label-button';
import PrintLabelButton from './print-label-button';
import { transformDeliveryInformation, showPrintOrReprintButton } from './utils';

const VerticalSpacer = () => <div style={{ width: '100%', height: '72px' }} />;

interface IShippingProps extends IPrintLabelButton, WithStyles<typeof ShippingStyles> {}

function PrintShippingButtons(props: IShippingProps): JSX.Element | null {
  const {
    orderId,
    statusId,
    printDetails,
    currentPrintInformation,
    currentDeliveryInformation,
    currentFdcInformation,
    disabled,
    setFdcOrderDetails,
    isDuplicateOrder,
    updateShippingPage,
  } = props;

  const [pharmacyService] = React.useState<PharmacyService>(new PharmacyService());
  const [pharmacyInfo, setPharmacyInfo] = React.useState<IPharmacy | undefined>(props.pharmacyInfo);

  const { current, previous, keys } = useMemo(
    () => transformDeliveryInformation(currentDeliveryInformation),
    [currentDeliveryInformation],
  );

  useEffect(() => {
    const currentDispensingPharmacyNpi = currentDeliveryInformation?.dispensingPharmacyNpi;
    if (!currentDispensingPharmacyNpi) return;

    const getNpi = async (currentDispensingPharmacyNpi: string) => {
      const pharmacyResponse = await pharmacyService.getPharmacyByNpi(currentDispensingPharmacyNpi);
      setPharmacyInfo(pharmacyResponse || undefined);
    };
    getNpi(currentDispensingPharmacyNpi);
  }, [currentDeliveryInformation?.dispensingPharmacyNpi]);

  const allowPrintOrReprintLabelButton = showPrintOrReprintButton(
    pharmacyInfo?.workflow_type,
    currentFdcInformation?.statusId,
    currentDeliveryInformation?.deliveryMethod,
  );

  const displayShippingButtonsGroup = (): JSX.Element => {
    const showPrintLabelButton = !currentPrintInformation && allowPrintOrReprintLabelButton;
    const showReprintLabelButton = !!currentPrintInformation && allowPrintOrReprintLabelButton;
    return (
      <>
        {showPrintLabelButton ? (
          <PrintLabelButton
            currentDeliveryInformation={currentDeliveryInformation}
            currentPrintInformation={currentPrintInformation}
            currentFdcInformation={currentFdcInformation}
            orderId={orderId}
            disabled={disabled}
            pharmacyInfo={pharmacyInfo}
            updateShippingPage={updateShippingPage}
          />
        ) : null}
        {showReprintLabelButton ? (
          <ReprintLabelButton
            currentDeliveryInformation={currentDeliveryInformation}
            currentFdcInformation={currentFdcInformation}
            currentPrintInformation={currentPrintInformation}
            orderId={orderId}
            printDetails={printDetails}
            isDuplicateOrder={isDuplicateOrder}
            pharmacyInfo={pharmacyInfo}
            updateShippingPage={updateShippingPage}
            disabled={disabled}
          />
        ) : null}

        {showReprintLabelButton ? (
          <VoidLabelButton
            currentDeliveryInformation={currentDeliveryInformation}
            currentPrintInformation={currentPrintInformation}
            currentFdcInformation={currentFdcInformation}
            orderId={orderId}
            setFdcOrderDetails={setFdcOrderDetails}
            updateShippingPage={updateShippingPage}
            pharmacyInfo={pharmacyInfo}
            disabled={disabled}
          />
        ) : null}
      </>
    );
  };

  return <>{displayShippingButtonsGroup() || <VerticalSpacer />}</>;
}

export default withStyles(ShippingStyles)(PrintShippingButtons);
